import React from 'react';
import { Link } from 'react-router-dom';
import { Smile, Sparkles, Award, ArrowRight } from 'lucide-react';
import BeforeAfterSlider, { useGallery } from './BeforeAfterSlider';

const treatmentLink = (treatment) => ({
  'Invisalign': '/invisalign-acton',
  'Composite Bonding': '/composite-bonding-acton',
  'Dental Implants': '/implants-acton',
  'Porcelain Veneers': '/services/porcelain-veneers',
  'Teeth Whitening': '/services/teeth-whitening',
  'Crowns': '/services/crowns',
  'Full Mouth Reconstruction': '/services/full-mouth-reconstruction',
  'Emergency Repair': '/emergency-dentist-acton',
  'Dentures': '/services/dentures',
  'Bridges': '/services/bridges',
  'Smile Makeover': '/cosmetic-dentist-acton',
}[treatment] || '/before-after');

export const CaseCard = ({ item, showLink = true }) => (
  <figure className="rounded-2xl border-2 border-sky-gray/20 bg-white shadow-lg overflow-hidden" data-testid={`case-card-${item.id}`}>
    <BeforeAfterSlider before={item.before_url} after={item.after_url} alt={`${item.treatment}: ${item.title}`} width={item.width} height={item.height} />
    <figcaption className="p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#006A71]">{item.treatment}</span>
        {item.duration && <span className="text-xs text-chocolate-malt/80">{item.duration}</span>}
      </div>
      <h3 className="font-bold text-rabbit">{item.title}</h3>
      {item.caption && <p className="text-sm text-chocolate-malt mt-1">{item.caption}</p>}
      <p className="text-[11px] text-chocolate-malt/60 mt-2">Actual Restore.Dental patient, shown with written consent. Individual results vary.</p>
      {showLink && <Link to={treatmentLink(item.treatment)} className="inline-flex items-center gap-1 text-sm text-burnt-coral font-medium mt-2 hover:underline">About {item.treatment}<ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>}
    </figcaption>
  </figure>
);

const placeholders = [
  { treatment: 'Invisalign Treatment', icon: Smile },
  { treatment: 'Composite Bonding', icon: Sparkles },
  { treatment: 'Dental Implants', icon: Award },
];

/**
 * Grid of live before/after cases. Falls back to tasteful placeholders while the gallery is empty.
 */
const CaseGrid = ({ treatment, limit = 3, onCaseClick }) => {
  const { cases, loading } = useGallery(treatment);
  const shown = cases.slice(0, limit);
  if (!loading && shown.length === 0) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="case-grid-placeholder">
        {placeholders.map(({ treatment: t, icon: Icon }) => (
          <div key={t} className="aspect-square rounded-2xl border-2 border-sky-gray/20 bg-gradient-to-br from-sky-gray/10 to-burnt-coral/10 flex items-center justify-center">
            <div className="text-center p-8">
              <Icon className="w-16 h-16 text-burnt-coral mx-auto mb-4" aria-hidden="true" />
              <p className="text-rabbit font-semibold mb-2">{t}</p>
              <p className="text-sm text-chocolate-malt">Before & After photos available in practice</p>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="case-grid">
      {loading && shown.length === 0 && [0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-sky-gray/10 animate-pulse" />)}
      {shown.map((item) => (
        <div key={item.id} onClick={() => onCaseClick && onCaseClick(item.treatment)}>
          <CaseCard item={item} />
        </div>
      ))}
    </div>
  );
};

export default CaseGrid;
